import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  Copy,
  Check,
  Database,
  Download,
  FlaskConical,
  ListChecks,
  Loader2,
  Mail,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Undo2,
  XCircle,
} from 'lucide-react';
import { platformAPI } from '../../services/platformApi';
import { StatusBadge } from './TenantsPage';
import PlanPanel from './PlanPanel';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function Dot({ ok, warn }) {
  if (ok) return <CheckCircle className="h-4 w-4 text-green-600" />;
  if (warn) return <AlertCircle className="h-4 w-4 text-amber-500" />;
  return <XCircle className="h-4 w-4 text-red-500" />;
}

function Tile({ ok, warn, title, children }) {
  return (
    <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
      <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
        <Dot ok={ok} warn={warn} />
        {title}
      </div>
      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{children}</div>
    </div>
  );
}

const btn =
  'inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700';
const input =
  'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white';

const errorText = (e) =>
  e?.response?.data?.error || e?.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join(', ') || e?.message;

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

/** Every action the page can take, what it asks first, and what it calls. */
const ACTIONS = {
  suspend: {
    title: (t) => `Suspend ${t.slug}?`,
    body: 'Every request to this tenant will be refused with 404. Nothing is deleted. Every server applies it at once; if Platform setup shows the cache bus as not connected, a server can keep serving the tenant for up to a minute.',
    label: 'Suspend',
    danger: true,
    run: (slug) => platformAPI.suspend(slug),
  },
  reactivate: {
    title: (t) => `Reactivate ${t.slug}?`,
    body: 'The tenant is served again on the next request.',
    label: 'Reactivate',
    run: (slug) => platformAPI.reactivate(slug),
  },
  migrate: {
    title: (t) => `Run migrations for ${t.slug}?`,
    body: 'Applies any migration files this schema has not run yet, each in its own transaction. Files already in its ledger are skipped.',
    label: 'Run migrations',
    run: (slug) => platformAPI.migrate(slug),
  },
  delete: {
    title: (t) => `Delete ${t.slug}?`,
    body: 'Staged. The tenant is suspended now and its schema and database role are dropped by the daily sweep once the hold passes. Cancel any time before then and nothing is lost. Export first if you want a copy.',
    label: 'Schedule deletion',
    danger: true,
    run: (slug, extra) => platformAPI.scheduleDelete(slug, extra),
  },
  cancelDelete: {
    title: (t) => `Cancel the deletion of ${t.slug}?`,
    body: 'Clears the hold. The tenant stays suspended until you reactivate it.',
    label: 'Cancel deletion',
    run: (slug) => platformAPI.cancelDelete(slug),
  },
  sandbox: {
    title: (t) => `Create a sandbox of ${t.slug}?`,
    body: 'Copies every table, row, view, function and trigger into a second schema with its own role, in one transaction. The sandbox is registered as status "sandbox": no callbacks, no scheduled work, no email, no chat, no ConnectWise. Integration credentials are not copied. It can be signed into at <slug>-sandbox with the tenant\'s own accounts and passwords, under a banner that says what it is.',
    label: 'Create sandbox',
    run: (slug) => platformAPI.createSandbox(slug),
  },
  deleteSandbox: {
    title: (t) => `Delete the sandbox of ${t.slug}?`,
    body: 'Drops the sandbox schema and role immediately. There is no hold: it was a copy.',
    label: 'Delete sandbox',
    danger: true,
    run: (slug) => platformAPI.deleteSandbox(slug),
  },
  isolation: {
    title: (t) => `Check isolation for ${t.slug}?`,
    body: 'Connects as the tenant\'s own database role and proves three things: its tables are readable, the registry is refused, and no other schema is visible. The result and its timestamp are stored on the tenant.',
    label: 'Run check',
    run: (slug) => platformAPI.isolationCheck(slug),
  },
  usageRollup: {
    title: (t) => `Recompute usage for ${t.slug}?`,
    body: 'Counts yesterday\'s figures from the tenant\'s own tables and writes the row now, without waiting for the nightly rollup.',
    label: 'Recompute',
    run: (slug) => platformAPI.usageRollup(slug),
  },
  includedEmail: {
    title: (t) => `Give ${t.slug} included email?`,
    body: 'Creates an SMTP2GO subaccount for the tenant under the platform account, mints it an API key, and stores that as the tenant\'s Email delivery row sending from <slug>@ the platform mail domain. The tenant can send at once and may move to its own domain later from Admin → Integrations → Email delivery. A tenant that already set up its own sender is left alone.',
    label: 'Provision',
    run: (slug) => platformAPI.provisionIncludedEmail(slug),
  },
};

/**
 * One tenant: status, health tiles, and the four things an operator does to
 * it. Health only — the diagnostics endpoint carries counts and error
 * strings, never a ticket or a user, and this page shows nothing it does
 * not send.
 */
export default function TenantDetailPage() {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(null); // a key of ACTIONS
  const [holdDays, setHoldDays] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: '', firstName: '', lastName: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const detail = useQuery({
    queryKey: ['platform-tenant', slug],
    queryFn: () => platformAPI.getTenant(slug),
    select: (r) => r.data,
  });

  const diagnostics = useQuery({
    queryKey: ['platform-tenant-diagnostics', slug],
    queryFn: () => platformAPI.diagnostics(slug),
    select: (r) => r.data,
    refetchInterval: 30000,
  });

  const usage = useQuery({
    queryKey: ['platform-tenant-usage', slug],
    queryFn: () => platformAPI.usage(slug, 30),
    select: (r) => r.data,
  });

  const setup = useQuery({
    queryKey: ['platform-tenant-setup', slug],
    queryFn: () => platformAPI.setup(slug),
    select: (r) => r.data,
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['platform-tenant', slug] });
    queryClient.invalidateQueries({ queryKey: ['platform-tenant-diagnostics', slug] });
    queryClient.invalidateQueries({ queryKey: ['platform-tenant-usage', slug] });
    queryClient.invalidateQueries({ queryKey: ['platform-tenant-setup', slug] });
    queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
  };

  const action = useMutation({
    mutationFn: ({ kind, extra }) => ACTIONS[kind].run(slug, extra),
    onSuccess: (r, { kind }) => {
      setActionResult({ kind, data: r.data });
      setConfirm(null);
      refreshAll();
    },
  });

  // The dump arrives as a blob with the token in a header, so the browser
  // cannot simply follow a link; hand it the file ourselves.
  const exportDump = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const r = await platformAPI.exportDump(slug);
      const match = /filename="([^"]+)"/.exec(r.headers?.['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || `${slug}.dump`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      refreshAll();
    } catch (e) {
      // A blob error body has to be read before it can be shown.
      let message = e?.message;
      try {
        if (e?.response?.data instanceof Blob) message = JSON.parse(await e.response.data.text()).error || message;
      } catch {
        /* keep the generic message */
      }
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  const inviteMutation = useMutation({
    mutationFn: (body) => platformAPI.invite(slug, body),
    onSuccess: (r) => {
      setInviteResult(r.data);
      setShowInvite(false);
      setInvite({ email: '', firstName: '', lastName: '' });
      refreshAll();
    },
  });

  const tenant = detail.data?.tenant;
  const d = diagnostics.data;

  if (detail.isLoading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }
  if (detail.error || !tenant) {
    return (
      <div className="space-y-4">
        <Link to="/manage" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-300">
          <ArrowLeft className="h-4 w-4" /> Tenants
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {errorText(detail.error) || 'Unknown tenant'}
        </div>
      </div>
    );
  }

  const cw = d?.integrations?.find?.((i) => i.provider === 'connectwise');
  const anet = d?.integrations?.find?.((i) => i.provider === 'authorizenet');
  const migrations = d?.migrations;
  const jobs = d?.jobs;
  const users = d?.users;
  const tz = d?.timezone;

  return (
    <div className="space-y-6">
      <Link to="/manage" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-300">
        <ArrowLeft className="h-4 w-4" /> Tenants
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{tenant.name}</h1>
            <StatusBadge status={tenant.status} />
          </div>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            <code>{tenant.slug}</code> · schema <code>{tenant.schemaName}</code> · role <code>{tenant.dbRole}</code>
          </p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Provisioned {fmt(tenant.provisionedAt)} · last migrated {fmt(tenant.migratedAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={btn} onClick={refreshAll}>
            <RefreshCw className={`h-4 w-4 ${diagnostics.isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
          {tenant.status === 'active' && (
            <button type="button" className={btn} onClick={() => setConfirm('suspend')}>
              <Pause className="h-4 w-4" /> Suspend
            </button>
          )}
          {tenant.status === 'suspended' && (
            <button type="button" className={btn} onClick={() => setConfirm('reactivate')}>
              <Play className="h-4 w-4" /> Reactivate
            </button>
          )}
          <button type="button" className={btn} onClick={() => setConfirm('migrate')}>
            <Database className="h-4 w-4" /> Run migrations
          </button>
          <button type="button" className={btn} disabled={exporting} onClick={exportDump} title="pg_dump of this tenant's schema, as one file">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
          </button>
          <button type="button" className={btn} onClick={() => setConfirm('isolation')}>
            <ShieldCheck className="h-4 w-4" /> Check isolation
          </button>
          {tenant.status === 'active' && !tenant.sandboxOf && (
            <button type="button" className={btn} onClick={() => setConfirm('sandbox')}>
              <FlaskConical className="h-4 w-4" /> Create sandbox
            </button>
          )}
          {tenant.status === 'sandbox' && (
            <button type="button" className={btn} onClick={() => setConfirm('deleteSandbox')}>
              <Trash2 className="h-4 w-4" /> Delete sandbox
            </button>
          )}
          {tenant.status !== 'sandbox' && tenant.status !== 'provisioning' && !tenant.deletedHoldUntil && (
            <button type="button" className={`${btn} !border-red-300 !text-red-700 dark:!border-red-800 dark:!text-red-300`} onClick={() => setConfirm('delete')}>
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
          {tenant.deletedHoldUntil && (
            <button type="button" className={btn} onClick={() => setConfirm('cancelDelete')}>
              <Undo2 className="h-4 w-4" /> Cancel deletion
            </button>
          )}
          <button
            type="button"
            disabled={tenant.status !== 'active'}
            onClick={() => setShowInvite(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Mail className="h-4 w-4" /> Invite admin
          </button>
        </div>
      </div>

      {tenant.notes && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          {tenant.notes}
        </div>
      )}

      {tenant.deletedHoldUntil && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <Trash2 className="h-4 w-4 flex-shrink-0" />
          <span>
            <strong>Scheduled for deletion.</strong> The schema and role are dropped by the daily sweep after {fmt(tenant.deletedHoldUntil)}.
            Cancel before then to keep everything.
          </span>
        </div>
      )}

      {tenant.status === 'sandbox' && (
        <div className="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
          <FlaskConical className="h-4 w-4 flex-shrink-0" />
          <span>
            <strong>Sandbox.</strong> A copy of another tenant. It can be signed into with the parent's accounts and shows a banner saying so; it receives no callbacks, runs no scheduled work, sends no email, posts to no chat workspace and holds no ConnectWise or payment credentials. A forgotten sandbox password is reset on the parent and the sandbox re-cloned.
          </span>
        </div>
      )}

      {exportError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="h-4 w-4" /> Export failed: {exportError}
        </div>
      )}

      {action.error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="h-4 w-4" /> {errorText(action.error)}
        </div>
      )}

      {actionResult && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm dark:border-green-800 dark:bg-green-900/30">
          <div className="flex items-start justify-between gap-4">
            <div className="text-green-800 dark:text-green-300">
              {actionResult.kind === 'migrate' && (
                <>
                  <strong>Migrations:</strong> {actionResult.data.applied} applied, {actionResult.data.present} already present.
                  {actionResult.data.log?.length > 0 && (
                    <pre className="mt-2 max-h-40 overflow-auto rounded bg-white p-2 font-mono text-xs text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                      {actionResult.data.log.join('\n')}
                    </pre>
                  )}
                </>
              )}
              {actionResult.kind === 'sandbox' && (
                <>
                  <strong>Sandbox {actionResult.data.sandbox?.slug} ready.</strong>{' '}
                  {actionResult.data.counts?.tables} tables, {actionResult.data.counts?.rows} rows copied.{' '}
                  <Link to={`/manage/tenants/${actionResult.data.sandbox?.slug}`} className="underline">
                    Open it
                  </Link>
                  .
                </>
              )}
              {actionResult.kind === 'deleteSandbox' && (
                <>
                  <strong>Sandbox {actionResult.data.deleted} dropped</strong> ({actionResult.data.steps?.join(', ')}).
                </>
              )}
              {actionResult.kind === 'isolation' && (
                <>
                  <strong>Isolation {actionResult.data.ok ? 'verified' : 'FAILED'}</strong> at {fmt(actionResult.data.checkedAt)}:{' '}
                  own schema {actionResult.data.detail?.own}; registry {actionResult.data.detail?.registry};{' '}
                  {actionResult.data.detail?.otherSchemas} foreign tables visible.
                </>
              )}
              {actionResult.kind === 'usageRollup' && (
                <>
                  <strong>Usage for {actionResult.data.usage?.day} recomputed.</strong> {actionResult.data.usage?.users} users,{' '}
                  {actionResult.data.usage?.emailsSent} emails, {actionResult.data.usage?.apiCalls} API calls.
                </>
              )}
              {['suspend', 'reactivate', 'delete', 'cancelDelete'].includes(actionResult.kind) && (
                <>
                  <strong>Tenant is now {actionResult.data.tenant?.status}.</strong> {actionResult.data.note}
                </>
              )}
            </div>
            <button type="button" onClick={() => setActionResult(null)} className="text-green-600 hover:text-green-800">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {inviteResult && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/30">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 text-sm text-green-800 dark:text-green-300">
              <strong>
                {inviteResult.resent ? 'Invitation re-sent' : 'Admin invited'}: {inviteResult.invited.email}
              </strong>
              <p className="mt-1">
                {inviteResult.emailed
                  ? `Emailed with this tenant's branding. The link expires ${fmt(inviteResult.expiresAt)}.`
                  : `The email ${inviteResult.emailError ? `failed (${inviteResult.emailError})` : 'was not sent (dev mode)'}. Hand this link over yourself — it sets their password and is shown once.`}
              </p>
              {inviteResult.setupLink && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 break-all rounded border border-green-300 bg-white px-3 py-2 font-mono text-xs dark:border-green-700 dark:bg-neutral-800 dark:text-green-300">
                    {inviteResult.setupLink}
                  </code>
                  <button
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(inviteResult.setupLink);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="rounded-lg bg-green-600 p-2 text-white hover:bg-green-700"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
            <button type="button" onClick={() => setInviteResult(null)} className="text-green-600 hover:text-green-800">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Plan and add-ons (Phase 7a) */}
      <PlanPanel tenant={tenant} onSaved={refreshAll} />

      {/* Health tiles */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Health {d?.checkedAt && <span className="font-normal text-neutral-400">· checked {fmt(d.checkedAt)}</span>}
        </h2>
        {diagnostics.error && (
          <p className="mb-2 text-sm text-red-600 dark:text-red-400">{errorText(diagnostics.error)}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Tile ok={cw?.enabled && !cw?.lastError} warn={!cw || !!cw?.lastError} title="ConnectWise">
            {cw
              ? `${cw.siteUrl || 'site not set'} · ${cw.enabled ? 'enabled' : 'disabled'}${cw.hasSecrets ? '' : ' · no credentials'}`
              : 'Not configured — this tenant relies on the environment, which blocks adding another tenant.'}
            {cw?.lastError && <p className="mt-1 text-amber-600 dark:text-amber-400">{cw.lastError} ({fmt(cw.lastErrorAt)})</p>}
          </Tile>
          <Tile ok={anet?.enabled} warn={!anet} title="Authorize.net">
            {anet ? `${anet.environment || 'environment not set'} · ${anet.enabled ? 'enabled' : 'disabled'}` : 'Not configured'}
          </Tile>
          <Tile
            ok={migrations && !migrations.error && migrations.pending?.length === 0}
            warn={migrations?.pending?.length > 0 || migrations?.error}
            title="Schema"
          >
            {migrations?.error
              ? migrations.error
              : migrations
                ? `${migrations.applied} of ${migrations.available} migrations · ${migrations.pending?.length || 0} pending${migrations.lastApplied ? ` · last ${migrations.lastApplied.filename}` : ''}`
                : '—'}
          </Tile>
          <Tile ok={jobs && !jobs.error && jobs.failed === 0} warn={jobs?.failed > 0 || jobs?.error} title="Background queue">
            {jobs?.error ? jobs.error : jobs ? `${jobs.pending} pending · ${jobs.running} running · ${jobs.failed} failed` : '—'}
          </Tile>
          <Tile ok={users && !users.error && users.admins > 0} warn={users?.admins === 0 || users?.error} title="Users">
            {users?.error
              ? users.error
              : users
                ? `${users.total} total · ${users.admins} admin${users.admins === 1 ? '' : 's'} · ${users.unverified} unverified · last login ${fmt(users.lastLoginAt)}`
                : '—'}
          </Tile>
          <Tile ok={tz?.configured} warn={!tz?.configured} title="Timezone">
            {tz?.error ? tz.error : tz?.configured ? tz.timezone : 'Not set — dates fall back to the platform default'}
          </Tile>
        </div>
        {d?.schema?.error && (
          <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">Could not read the tenant schema: {d.schema.error}</p>
        )}
      </section>

      {/* Setup, isolation, usage */}
      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
            <ListChecks className="h-4 w-4" /> Setup
            {setup.data && (
              <span className="font-normal text-neutral-400">
                · {setup.data.complete} of {setup.data.total}
              </span>
            )}
          </h2>
          <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            {setup.error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{errorText(setup.error)}</p>
            ) : setup.data ? (
              <ul className="space-y-2">
                {setup.data.steps.map((step) => (
                  <li key={step.key} className="flex items-start gap-2 text-sm">
                    <Dot ok={step.done} warn={!step.done} />
                    <div>
                      <div className="text-neutral-900 dark:text-white">{step.label}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">{step.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
            )}
          </div>
        </div>

        <div>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
            <ShieldCheck className="h-4 w-4" /> Isolation
          </h2>
          <Tile
            ok={tenant.isolation?.ok === true}
            warn={!tenant.isolation}
            title={tenant.isolation ? (tenant.isolation.ok ? 'Verified' : 'FAILED') : 'Never checked from the console'}
          >
            {tenant.isolation ? (
              <>
                <p>Checked {fmt(tenant.isolation.checkedAt)}</p>
                {tenant.isolation.detail && (
                  <p className="mt-1">
                    own schema: {tenant.isolation.detail.own} · registry: {tenant.isolation.detail.registry} ·{' '}
                    {tenant.isolation.detail.otherSchemas} foreign tables visible
                  </p>
                )}
              </>
            ) : (
              'Provisioning ran the check once; run it again any time to prove this role still sees nothing outside its schema.'
            )}
            <button type="button" className={`${btn} mt-2`} onClick={() => setConfirm('isolation')}>
              <ShieldCheck className="h-4 w-4" /> Check now
            </button>
          </Tile>
        </div>

        <div>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
            <BarChart3 className="h-4 w-4" /> Usage, last 30 days
          </h2>
          <div className="rounded-lg border border-neutral-200 p-3 text-xs text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
            {usage.error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{errorText(usage.error)}</p>
            ) : usage.data?.summary?.days > 0 ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                <dt>Users</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{usage.data.summary.users}</dd>
                <dt>Companies</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{usage.data.summary.companies}</dd>
                <dt>Tickets touched</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{usage.data.summary.ticketsTouched}</dd>
                <dt>Emails sent</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{usage.data.summary.emailsSent}</dd>
                <dt>API calls</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{usage.data.summary.apiCalls}</dd>
                <dt>Storage</dt>
                <dd className="text-right font-medium text-neutral-900 dark:text-white">{fmtBytes(usage.data.summary.storageBytes)}</dd>
                <dt className="col-span-2 mt-1 text-neutral-400">
                  {usage.data.summary.days} day{usage.data.summary.days === 1 ? '' : 's'} recorded · latest {usage.data.summary.latestDay}
                </dt>
              </dl>
            ) : (
              <p>No usage rows yet. The rollup runs once a day per tenant.</p>
            )}
            {tenant.status !== 'sandbox' && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className={btn} onClick={() => setConfirm('usageRollup')}>
                  <RefreshCw className="h-4 w-4" /> Recompute yesterday
                </button>
                <button type="button" className={btn} onClick={() => setConfirm('includedEmail')}>
                  <RefreshCw className="h-4 w-4" /> Provision included email
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {usage.data?.rows?.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">Daily usage</h3>
          <div className="max-h-64 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
                <tr>
                  {['Day', 'Users', 'Companies', 'Tickets', 'Emails', 'API calls', 'Storage'].map((h) => (
                    <th key={h} className="px-3 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {[...usage.data.rows].reverse().map((r) => (
                  <tr key={r.day}>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.day}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.users}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.companies}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.ticketsTouched}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.emailsSent}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{r.apiCalls}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{fmtBytes(r.storageBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Failed jobs and recent errors */}
      {(d?.failedJobs?.length > 0 || d?.recentErrors?.length > 0) && (
        <section className="grid gap-6 lg:grid-cols-2">
          {d?.failedJobs?.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">Failed jobs</h3>
              <div className="max-h-64 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
                    <tr>
                      <th className="px-3 py-2">Kind</th>
                      <th className="px-3 py-2">Attempts</th>
                      <th className="px-3 py-2">Error</th>
                      <th className="px-3 py-2">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {d.failedJobs.map((j) => (
                      <tr key={j.id}>
                        <td className="px-3 py-2 dark:text-neutral-200">{j.kind}</td>
                        <td className="px-3 py-2 dark:text-neutral-200">{j.attempts}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400">{j.lastError}</td>
                        <td className="px-3 py-2 text-neutral-500 dark:text-neutral-400">{fmt(j.finishedAt || j.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {d?.recentErrors?.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">Recent alert errors</h3>
              <ul className="max-h-64 space-y-1 overflow-auto rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-700">
                {d.recentErrors.map((e, i) => (
                  <li key={i} className="text-neutral-700 dark:text-neutral-300">
                    <span className="text-neutral-400">{fmt(e.createdAt)}</span> · {e.action} · {e.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Audit */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">Operator actions</h2>
        {detail.data.recentAudit?.length ? (
          <div className="max-h-72 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Operator</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {detail.data.recentAudit.map((a) => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 text-neutral-500 dark:text-neutral-400">{fmt(a.created_at)}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{a.operator_email || '—'}</td>
                    <td className="px-3 py-2 dark:text-neutral-200">{a.action}</td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-600 dark:text-neutral-300">{JSON.stringify(a.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing yet.</p>
        )}
      </section>

      {/* Confirmations */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-800 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">{ACTIONS[confirm].title(tenant)}</h2>
            <p className="mt-2 text-neutral-600 dark:text-neutral-400">{ACTIONS[confirm].body}</p>
            {confirm === 'delete' && (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Hold, in days (at least 1)</label>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={holdDays}
                  onChange={(e) => setHoldDays(e.target.value)}
                  placeholder="30 (the platform default)"
                  className={input}
                />
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={action.isPending}
                className="rounded-lg px-4 py-2 text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  action.mutate({
                    kind: confirm,
                    extra: confirm === 'delete' && holdDays !== '' ? { holdDays: Number(holdDays) } : {},
                  })
                }
                disabled={action.isPending}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-white disabled:opacity-50 ${
                  ACTIONS[confirm].danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {action.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {ACTIONS[confirm].label}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-neutral-800 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-neutral-200 p-6 dark:border-neutral-700">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Invite the first admin</h2>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Creates an <code>admin</code> in this tenant and emails them a link to set their password, branded as
                this tenant and on its own hostname.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                inviteMutation.mutate({
                  email: invite.email.trim(),
                  firstName: invite.firstName.trim() || undefined,
                  lastName: invite.lastName.trim() || undefined,
                });
              }}
              className="space-y-4 p-6"
            >
              {inviteMutation.error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
                  <AlertCircle className="h-4 w-4" /> {errorText(inviteMutation.error)}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Email *</label>
                <input type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={input} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">First name</label>
                  <input type="text" value={invite.firstName} onChange={(e) => setInvite({ ...invite, firstName: e.target.value })} className={input} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Last name</label>
                  <input type="text" value={invite.lastName} onChange={(e) => setInvite({ ...invite, lastName: e.target.value })} className={input} />
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  disabled={inviteMutation.isPending}
                  className="rounded-lg px-4 py-2 text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending || !invite.email.trim()}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {inviteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
